import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Collapse,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Drawer,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import UndoIcon from '@mui/icons-material/Undo'
import CompressIcon from '@mui/icons-material/Compress'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import AddLocationIcon from '@mui/icons-material/AddLocation'
import PanToolIcon from '@mui/icons-material/PanTool'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong'
import ListIcon from '@mui/icons-material/List'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { MapContainer } from '@components/map/MapContainer'
import { Marker, Polyline, useMapEvents, useMap, CircleMarker, Popup } from 'react-leaflet'
import L from 'leaflet'
import { simplifyRoute } from '@features/adventures/utils/gpxParser'

// Edit modes
type EditMode = 'move' | 'add' | 'delete' | 'split'

// Component to fit map bounds on mount and provide recenter function
function FitBoundsOnMount({ bounds, onMapReady }: {
  bounds: [[number, number], [number, number]]
  onMapReady?: (recenter: () => void) => void
}) {
  const map = useMap()
  const hasFitted = useRef(false)

  const recenter = useCallback(() => {
    try {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 })
    } catch (e) {
      // Ignore if map is not ready
    }
  }, [map, bounds])

  useEffect(() => {
    if (!hasFitted.current && bounds) {
      hasFitted.current = true
      // Small delay to ensure map is fully initialized
      setTimeout(() => {
        recenter()
      }, 100)
    }
  }, [bounds, recenter])

  // Expose recenter function to parent
  useEffect(() => {
    if (onMapReady) {
      onMapReady(recenter)
    }
  }, [onMapReady, recenter])

  return null
}

interface RoutePointEditorProps {
  open: boolean
  onClose: () => void
  routePoints: [number, number][]
  onSave: (points: [number, number][]) => void
  sourceLocation?: { lat: number; lng: number; label?: string }
  destinationLocation?: { lat: number; lng: number; label?: string }
  lineColor?: string
}

// Create custom marker icons for source/destination
const createLocationIcon = (color: string, label: string) => L.divIcon({
  className: 'custom-location-marker',
  html: `<div style="
    background: ${color};
    width: 32px;
    height: 32px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
  ">
    <span style="
      transform: rotate(45deg);
      color: white;
      font-weight: bold;
      font-size: 12px;
    ">${label}</span>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
})

// Draggable waypoint marker (default - purple)
const waypointIcon = L.divIcon({
  className: 'waypoint-marker',
  html: `<div style="
    background: #9C27B0;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    cursor: grab;
  "></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
})

// Start waypoint marker (green) - double size
const startWaypointIcon = L.divIcon({
  className: 'waypoint-marker-start',
  html: `<div style="
    background: #4CAF50;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    cursor: grab;
  "></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
})

// End waypoint marker (red) - double size
const endWaypointIcon = L.divIcon({
  className: 'waypoint-marker-end',
  html: `<div style="
    background: #f44336;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    cursor: grab;
  "></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
})

// Delete mode waypoint marker (red with X)
const deleteWaypointIcon = L.divIcon({
  className: 'waypoint-marker-delete',
  html: `<div style="
    background: #f44336;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    cursor: pointer;
  "></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
})

// Helper to get waypoint icon based on position
function getWaypointIcon(index: number, total: number, mode: EditMode) {
  if (mode === 'delete') return deleteWaypointIcon
  if (total === 1) return startWaypointIcon // Single point is start
  if (index === 0) return startWaypointIcon
  if (index === total - 1) return endWaypointIcon
  return waypointIcon
}

// Animated polyline with directional arrows
function AnimatedDirectionalPolyline({
  positions,
  color,
}: {
  positions: [number, number][]
  color: string
}) {
  const polylineRef = useRef<L.Polyline | null>(null)

  // Inject CSS keyframes for arrow animation
  useEffect(() => {
    const styleId = 'route-arrow-animation'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        @keyframes arrowFlow {
          from { stroke-dashoffset: 24; }
          to { stroke-dashoffset: 0; }
        }
        .animated-route-line {
          stroke-dasharray: 1, 12;
          animation: arrowFlow 0.8s linear infinite;
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  useEffect(() => {
    if (polylineRef.current) {
      const element = polylineRef.current.getElement()
      if (element) {
        element.classList.add('animated-route-line')
      }
    }
  }, [positions])

  return (
    <Polyline
      ref={polylineRef}
      positions={positions}
      pathOptions={{
        color,
        weight: 4,
        opacity: 0.8,
        lineCap: 'round',
        lineJoin: 'round',
      }}
    />
  )
}

// Arrow markers along the route to show direction
function DirectionalArrows({
  positions,
  color,
}: {
  positions: [number, number][]
  color: string
}) {
  // Calculate midpoints and angles for each segment
  const arrows = useMemo(() => {
    if (positions.length < 2) return []

    const result: { position: [number, number]; angle: number }[] = []

    for (let i = 0; i < positions.length - 1; i++) {
      const start = positions[i]
      const end = positions[i + 1]

      // Calculate midpoint
      const midLat = (start[0] + end[0]) / 2
      const midLng = (start[1] + end[1]) / 2

      // Calculate angle in degrees
      const angle = Math.atan2(end[1] - start[1], end[0] - start[0]) * (180 / Math.PI)

      result.push({
        position: [midLat, midLng],
        angle: angle - 90, // Adjust for arrow pointing direction
      })
    }

    return result
  }, [positions])

  return (
    <>
      {arrows.map((arrow, index) => (
        <Marker
          key={`arrow-${index}`}
          position={arrow.position}
          icon={L.divIcon({
            className: 'direction-arrow',
            html: `<div style="
              font-size: 16px;
              color: ${color};
              transform: rotate(${arrow.angle}deg);
              text-shadow: 0 0 2px white, 0 0 2px white;
              line-height: 1;
              pointer-events: none;
            ">▶</div>`,
            iconSize: [16, 16],
            iconAnchor: [8, 8],
          })}
          interactive={false}
        />
      ))}
    </>
  )
}

interface MapEventsProps {
  onMapClick: (lat: number, lng: number) => void
  onLineClick: (lat: number, lng: number, segmentIndex: number) => void
  onSplitSegment: (segmentIndex: number) => void
  routePoints: [number, number][]
  mode: EditMode
}

function MapEvents({ onMapClick, onLineClick, onSplitSegment, routePoints, mode }: MapEventsProps) {
  const map = useMap()

  useMapEvents({
    click: (e) => {
      // Only handle clicks in add or split mode
      if (mode !== 'add' && mode !== 'split') return

      // Check if click is near the polyline
      const clickPoint = e.latlng
      let nearestSegment = -1
      let minDistance = Infinity

      for (let i = 0; i < routePoints.length - 1; i++) {
        const start = L.latLng(routePoints[i][0], routePoints[i][1])
        const end = L.latLng(routePoints[i + 1][0], routePoints[i + 1][1])

        // Calculate distance from click to line segment
        const distance = distanceToSegment(map, clickPoint, start, end)
        if (distance < minDistance) {
          minDistance = distance
          nearestSegment = i
        }
      }

      if (mode === 'split') {
        // In split mode, only act when clicking on a segment
        if (minDistance < 20 && nearestSegment >= 0) {
          onSplitSegment(nearestSegment)
        }
      } else {
        // In add mode, insert at click location or append
        if (minDistance < 20 && nearestSegment >= 0) {
          onLineClick(clickPoint.lat, clickPoint.lng, nearestSegment + 1)
        } else {
          onMapClick(clickPoint.lat, clickPoint.lng)
        }
      }
    },
  })
  return null
}

function distanceToSegment(map: L.Map, point: L.LatLng, start: L.LatLng, end: L.LatLng): number {
  const p = map.latLngToLayerPoint(point)
  const s = map.latLngToLayerPoint(start)
  const e = map.latLngToLayerPoint(end)

  const dx = e.x - s.x
  const dy = e.y - s.y
  const len2 = dx * dx + dy * dy

  if (len2 === 0) return p.distanceTo(s)

  let t = ((p.x - s.x) * dx + (p.y - s.y) * dy) / len2
  t = Math.max(0, Math.min(1, t))

  const closest = L.point(s.x + t * dx, s.y + t * dy)
  return p.distanceTo(closest)
}

export function RoutePointEditor({
  open,
  onClose,
  routePoints: initialPoints,
  onSave,
  sourceLocation,
  destinationLocation,
  lineColor = '#9C27B0',
}: RoutePointEditorProps) {
  const [points, setPoints] = useState<[number, number][]>([])
  const [history, setHistory] = useState<[number, number][][]>([])
  const [selectedPointIndex, setSelectedPointIndex] = useState<number | null>(null)
  const [mode, setMode] = useState<EditMode>('move')
  const [showHelp, setShowHelp] = useState(false)
  const [showPointsList, setShowPointsList] = useState(false)
  const recenterFnRef = useRef<(() => void) | null>(null)

  // Initialize points when dialog opens
  useEffect(() => {
    if (open) {
      setPoints([...initialPoints])
      setHistory([])
      setSelectedPointIndex(null)
      setMode('move')
      setShowHelp(false)
    }
  }, [open, initialPoints])

  // Calculate map bounds - use initialPoints as fallback since state may not be set yet
  const bounds = useMemo(() => {
    // Use points if available, otherwise fall back to initialPoints
    const routePoints = points.length > 0 ? points : initialPoints
    const allPoints: [number, number][] = [...routePoints]

    if (sourceLocation) allPoints.push([sourceLocation.lat, sourceLocation.lng])
    if (destinationLocation) allPoints.push([destinationLocation.lat, destinationLocation.lng])

    if (allPoints.length === 0) {
      // Default fallback - should rarely happen
      return [[40.7128, -74.006], [40.7228, -73.996]] as [[number, number], [number, number]]
    }

    const lats = allPoints.map(p => p[0])
    const lngs = allPoints.map(p => p[1])

    // Calculate dynamic padding based on extent
    const latExtent = Math.max(...lats) - Math.min(...lats)
    const lngExtent = Math.max(...lngs) - Math.min(...lngs)
    const padding = Math.max(0.005, Math.max(latExtent, lngExtent) * 0.1)

    return [
      [Math.min(...lats) - padding, Math.min(...lngs) - padding],
      [Math.max(...lats) + padding, Math.max(...lngs) + padding],
    ] as [[number, number], [number, number]]
  }, [points, initialPoints, sourceLocation, destinationLocation])

  const pushToHistory = useCallback(() => {
    setHistory(prev => [...prev, [...points]])
  }, [points])

  const handleUndo = useCallback(() => {
    if (history.length > 0) {
      const previousState = history[history.length - 1]
      setHistory(prev => prev.slice(0, -1))
      setPoints(previousState)
    }
  }, [history])

  const handleMapClick = useCallback((lat: number, lng: number) => {
    if (mode !== 'add') return
    pushToHistory()
    setPoints(prev => [...prev, [lat, lng]])
  }, [mode, pushToHistory])

  const handleLineClick = useCallback((lat: number, lng: number, insertIndex: number) => {
    if (mode !== 'add') return
    pushToHistory()
    setPoints(prev => {
      const newPoints = [...prev]
      newPoints.splice(insertIndex, 0, [lat, lng])
      return newPoints
    })
  }, [mode, pushToHistory])

  const handlePointDrag = useCallback((index: number, lat: number, lng: number) => {
    setPoints(prev => {
      const newPoints = [...prev]
      newPoints[index] = [lat, lng]
      return newPoints
    })
  }, [])

  const handlePointDragStart = useCallback(() => {
    pushToHistory()
  }, [pushToHistory])

  const handleDeletePoint = useCallback((index: number) => {
    pushToHistory()
    setPoints(prev => prev.filter((_, i) => i !== index))
    setSelectedPointIndex(null)
  }, [pushToHistory])

  const handlePointClick = useCallback((index: number) => {
    if (mode === 'delete') {
      handleDeletePoint(index)
    } else {
      setSelectedPointIndex(index)
    }
  }, [mode, handleDeletePoint])

  const handleClearAll = useCallback(() => {
    pushToHistory()
    setPoints([])
  }, [pushToHistory])

  const handleSimplify = useCallback(() => {
    if (points.length < 3) return
    pushToHistory()
    const simplified = simplifyRoute(points, 15) // 15 meter tolerance
    setPoints(simplified)
  }, [points, pushToHistory])

  // Split a segment by inserting a midpoint between two consecutive points
  const handleSplitSegment = useCallback((segmentIndex: number) => {
    if (segmentIndex < 0 || segmentIndex >= points.length - 1) return
    pushToHistory()
    const p1 = points[segmentIndex]
    const p2 = points[segmentIndex + 1]
    const midpoint: [number, number] = [
      (p1[0] + p2[0]) / 2,
      (p1[1] + p2[1]) / 2
    ]
    setPoints(prev => {
      const newPoints = [...prev]
      newPoints.splice(segmentIndex + 1, 0, midpoint)
      return newPoints
    })
  }, [points, pushToHistory])

  const handleSave = useCallback(() => {
    onSave(points)
    onClose()
  }, [points, onSave, onClose])

  const handleModeChange = useCallback((_: React.MouseEvent<HTMLElement>, newMode: EditMode | null) => {
    if (newMode !== null) {
      setMode(newMode)
      setSelectedPointIndex(null)
    }
  }, [])

  const handleMapReady = useCallback((recenterFn: () => void) => {
    recenterFnRef.current = recenterFn
  }, [])

  const handleRecenter = useCallback(() => {
    if (recenterFnRef.current) {
      recenterFnRef.current()
    }
  }, [])

  // Calculate distance
  const totalDistance = useMemo(() => {
    let total = 0
    for (let i = 1; i < points.length; i++) {
      const lat1 = points[i - 1][0] * Math.PI / 180
      const lat2 = points[i][0] * Math.PI / 180
      const dLat = (points[i][0] - points[i - 1][0]) * Math.PI / 180
      const dLon = (points[i][1] - points[i - 1][1]) * Math.PI / 180
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
      total += 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    }
    return total
  }, [points])

  const distanceDisplay = totalDistance >= 1000
    ? `${(totalDistance / 1000).toFixed(2)} km`
    : `${Math.round(totalDistance)} m`

  // Get mode description
  const modeDescription = {
    move: 'Pan and zoom the map',
    add: 'Click map to add points, click route line to insert',
    split: 'Click on route line to insert midpoint',
    delete: 'Click points to delete them',
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{ sx: { bgcolor: 'background.default' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Edit Route Points
        </Typography>
        <Chip label={`${points.length} points`} size="small" />
        <Chip label={distanceDisplay} size="small" color="primary" />
        <Tooltip title="Undo">
          <span>
            <IconButton onClick={handleUndo} disabled={history.length === 0} size="small">
              <UndoIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Simplify route (reduce points)">
          <span>
            <IconButton onClick={handleSimplify} disabled={points.length < 3} size="small">
              <CompressIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Clear all points">
          <span>
            <IconButton onClick={handleClearAll} disabled={points.length === 0} size="small" color="error">
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Points List">
          <IconButton onClick={() => setShowPointsList(!showPointsList)} size="small" color={showPointsList ? 'primary' : 'default'}>
            <ListIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Help">
          <IconButton onClick={() => setShowHelp(!showHelp)} size="small" color={showHelp ? 'primary' : 'default'}>
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', flexDirection: 'row' }}>
        {/* Points List Drawer */}
        <Collapse in={showPointsList} orientation="horizontal" sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              width: 280,
              height: '100%',
              borderRight: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2">Route Points</Typography>
              <Typography variant="caption" color="text.secondary">
                Click split icon to add point between segments
              </Typography>
            </Box>
            <List dense sx={{ flexGrow: 1, overflow: 'auto', py: 0 }}>
              {points.length === 0 ? (
                <ListItem>
                  <ListItemText
                    secondary="No points yet. Use Add mode to place points on the map."
                  />
                </ListItem>
              ) : (
                points.map((point, index) => (
                  <Box key={`point-group-${index}`}>
                    <ListItemButton
                      selected={selectedPointIndex === index}
                      onClick={() => setSelectedPointIndex(index)}
                      sx={{ py: 0.5 }}
                    >
                      <LocationOnIcon
                        sx={{
                          fontSize: 18,
                          mr: 1,
                          color: selectedPointIndex === index ? 'primary.main' : 'text.secondary'
                        }}
                      />
                      <ListItemText
                        primary={`Point ${index + 1}`}
                        secondary={`${point[0].toFixed(5)}, ${point[1].toFixed(5)}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption', sx: { fontFamily: 'monospace' } }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Delete point">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeletePoint(index)
                            }}
                            sx={{ opacity: 0.6, '&:hover': { opacity: 1, color: 'error.main' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItemButton>
                    {/* Split button between this point and the next */}
                    {index < points.length - 1 && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          py: 0.25,
                          borderTop: '1px dashed',
                          borderBottom: '1px dashed',
                          borderColor: 'divider',
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Tooltip title={`Split path between Point ${index + 1} and ${index + 2}`}>
                          <IconButton
                            size="small"
                            onClick={() => handleSplitSegment(index)}
                            sx={{
                              py: 0,
                              color: 'primary.main',
                              '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                            }}
                          >
                            <CallSplitIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                            <Typography variant="caption" sx={{ ml: 0.5 }}>Split</Typography>
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                ))
              )}
            </List>
          </Box>
        </Collapse>

        {/* Main content area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Collapse in={showHelp}>
          <Box sx={{ m: 1, p: 2, bgcolor: 'info.main', color: 'info.contrastText', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>How to edit route points:</Typography>
            <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2 }}>
              <li><strong>Move Mode:</strong> Pan and zoom the map without modifying points</li>
              <li><strong>Add Mode:</strong> Click on the map to add waypoints at the end, or click on the route line to insert a point</li>
              <li><strong>Split Mode:</strong> Click on any segment to insert a midpoint between the two endpoints</li>
              <li><strong>Delete Mode:</strong> Click on any point to remove it</li>
              <li>In Move/Add/Split mode, you can drag existing points to reposition them</li>
              <li>Use Simplify to reduce the number of points while maintaining the route shape</li>
            </Typography>
          </Box>
        </Collapse>

        <Box sx={{ flexGrow: 1, position: 'relative' }}>
          <MapContainer height="100%">
            <FitBoundsOnMount bounds={bounds} onMapReady={handleMapReady} />
            <MapEvents
              onMapClick={handleMapClick}
              onLineClick={handleLineClick}
              onSplitSegment={handleSplitSegment}
              routePoints={points}
              mode={mode}
            />

            {/* Source location marker */}
            {sourceLocation && (
              <Marker
                position={[sourceLocation.lat, sourceLocation.lng]}
                icon={createLocationIcon('#4CAF50', 'S')}
              >
                <Popup>{sourceLocation.label || 'Source'}</Popup>
              </Marker>
            )}

            {/* Destination location marker */}
            {destinationLocation && (
              <Marker
                position={[destinationLocation.lat, destinationLocation.lng]}
                icon={createLocationIcon('#F44336', 'D')}
              >
                <Popup>{destinationLocation.label || 'Destination'}</Popup>
              </Marker>
            )}

            {/* Route polyline with animated direction */}
            {points.length > 1 && (
              <AnimatedDirectionalPolyline positions={points} color={lineColor} />
            )}

            {/* Editable waypoint markers */}
            {points.map((point, index) => (
              <Marker
                key={`waypoint-${index}`}
                position={point}
                icon={getWaypointIcon(index, points.length, mode)}
                draggable={mode !== 'delete'}
                eventHandlers={{
                  dragstart: handlePointDragStart,
                  drag: (e) => {
                    const marker = e.target
                    const pos = marker.getLatLng()
                    handlePointDrag(index, pos.lat, pos.lng)
                  },
                  click: () => handlePointClick(index),
                }}
              />
            ))}

            {/* Highlight selected point */}
            {selectedPointIndex !== null && points[selectedPointIndex] && mode !== 'delete' && (
              <CircleMarker
                center={points[selectedPointIndex]}
                radius={20}
                color={lineColor}
                weight={2}
                fillOpacity={0.2}
              />
            )}
          </MapContainer>

          {/* Mode toolbar at top right */}
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 1000,
              p: 0.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              size="small"
              orientation="vertical"
            >
              <ToggleButton value="move">
                <Tooltip title="Move/Pan" placement="left">
                  <PanToolIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="add" color="primary">
                <Tooltip title="Add Points" placement="left">
                  <AddLocationIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="split" color="secondary">
                <Tooltip title="Split Segment (click on line to insert midpoint)" placement="left">
                  <ContentCutIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="delete" color="error">
                <Tooltip title="Delete Points" placement="left">
                  <DeleteForeverIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Recenter Map" placement="left">
              <IconButton onClick={handleRecenter} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                <CenterFocusStrongIcon />
              </IconButton>
            </Tooltip>
          </Paper>
        </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Route
        </Button>
      </DialogActions>
    </Dialog>
  )
}
